/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Modal, message, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  DbAttachment,
  DbCommit,
  DbFile,
  DbFolder,
  DbShare,
} from '~src/types/db';
import {
  createCommit,
  deleteAttachment,
  deleteFile,
  deleteFolder,
  updateCommit,
} from '~src/services/api';
import { appLog } from '~src/utils/log';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import { DB_STATE } from '~src/types/enums';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { useTenderId } from '~src/hooks/useTenderId';
import { updateShare } from '~src/services/api/shares';

export interface DeleteModalProps extends Partial<ModalProps> {
  folders?: DbFolder[];
  files?: DbFile[];
  attachments?: DbAttachment[];
  shares?: DbShare[];
  onDone?: () => void;
}

export const DeleteModal: React.FC<DeleteModalProps> = ({
  folders = [],
  files = [],
  attachments = [],
  shares = [],
  onCancel,
  onDone,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const tenderId = useTenderId();

  // console.log(
  //   `delete modal data:`,
  //   { projectId, tenderId },
  //   { shares, folders, files, attachments }
  // );

  const doCreateCommit = async () => {
    if (!projectId) throw new Error('Missing project id.');

    const commitData = {
      description: 'delete',
      is_important: false,
      state: DB_STATE.DRAFT,
    };

    return createCommit({
      projectId: String(projectId),
      commitData,
    }).catch((err) => {
      message.error(intl.formatMessage({ id: 'commit-create-failed' }));
      appLog('UploadPanel -> createCommit -> commit', commitData);
      appLog('UploadPanel -> createCommit -> err', err);
      throw err;
    });
  };

  const [doDeleteAll, { isLoading }] = useMutation(deleteAll, {
    onSuccess: async (deletedFolder, { commit }) => {
      // hide modal
      // @ts-ignore
      onDone ? onDone() : onCancel && onCancel();

      if (folders.length) {
        queryCache.invalidateQueries('folders');
      }

      if (files.length) {
        queryCache.invalidateQueries(
          QueryKeys.tenderTenderFoldersVersionedFiles
        );
        queryCache.invalidateQueries('files');
      }

      if (attachments.length) {
        queryCache.invalidateQueries(QueryKeys.tendersTenderAttachments);
      }

      if (shares.length) {
        queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
      }

      await updateCommit({
        projectId: String(projectId),
        commitData: { ...commit, state: DB_STATE.PROCESSED },
      });
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'delete-failed' }));
      appLog('DeleteModal -> deleteAll -> items', item);
      appLog('DeleteModal -> deleteAll -> err', error);
    },
  });

  if (
    modalProps.visible &&
    !files?.length &&
    !folders?.length &&
    !attachments?.length &&
    !shares?.length
  ) {
    console.warn('DeleteModal called with empty data sets.');
  }

  const handleSubmit = async () => {
    try {
      const commit = await doCreateCommit();
      await doDeleteAll({
        files,
        folders,
        attachments,
        shares,
        commit,
        tenderId,
        projectId,
      });
    } catch (err) {
      appLog('DeleteModal -> handleSubmit -> err', err);
      message.error(intl.formatMessage({ id: 'delete-failed' }));
    }
  };

  let singleEntity: DbFile | DbFolder | DbAttachment | DbShare | undefined;
  if (attachments.length) {
    singleEntity = attachments[0];
  } else if (shares.length <= 1 && !shares.length) {
    singleEntity = shares[0];
  } else if (files.length <= 1 && !folders.length) {
    singleEntity = files[0];
  } else if (folders.length <= 1 && !files.length) {
    singleEntity = folders[0];
  }

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'delete' })}
          icon={['far', 'trash']}
        />
      }
      okText={intl.formatMessage({ id: 'delete' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ danger: true, loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Typography.Paragraph css={styles.text}>
        {singleEntity
          ? intl.formatMessage(
              { id: 'delete-single-text' },
              {
                name:
                  (singleEntity as DbShare).share_key_title ||
                  (singleEntity as DbAttachment).name ||
                  (singleEntity as DbFile).title ||
                  (singleEntity as DbFolder).name,
              }
            )
          : intl.formatMessage({ id: 'delete-check' })}
      </Typography.Paragraph>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  text: {
    textAlign: 'center',
    margin: 0,
    fontSize: 15,
  },
});

interface DeleteAllParams {
  folders?: DbFolder[];
  files?: DbFile[];
  attachments?: DbAttachment[];
  shares?: DbShare[];
  commit: DbCommit;
  tenderId?: number | undefined;
  projectId?: number;
}

const deleteAll = async ({
  folders = [],
  files = [],
  attachments = [],
  shares = [],
  commit,
  tenderId,
  projectId,
}: DeleteAllParams): Promise<Array<DbFile | DbFolder>> => {
  const filesPromises = files.map((file) =>
    deleteFile({ projectId: String(file.project_id), file })
  );

  const foldersPromises = folders.map((folder) =>
    deleteFolder({
      projectId: String(folder.project_id),
      folder,
      commitId: commit.id,
    })
  );

  const attachmentsPromises = attachments.map((attachment) =>
    deleteAttachment({
      projectId: String(attachment.project_id),
      tenderId: String(tenderId),
      attachment,
    })
  );

  const sharesPromises = shares.map((share) =>
    updateShare({
      projectId: projectId,
      tenderId: tenderId,
      shareKey: share.share_key_id,
      action: 'remove',
    })
  );

  return Promise.all([
    ...filesPromises,
    ...foldersPromises,
    ...attachmentsPromises,
    ...sharesPromises,
  ] as Promise<DbFile | DbFolder>[]);
};
